if (!customElements.get("vehicle-progressive-filters")) {
  const activeVehicleTargetId = () =>
    new URLSearchParams(window.location.search)
      .getAll("filter.v.m.car.ids")
      .flatMap((value) => String(value || "").split(","))
      .map((value) => value.trim())
      .find((value) => /^\d+$/.test(value)) || "";

  const vehicleRemovalUrl = () => {
    const destination = new URL(window.location.href);
    destination.searchParams.delete("filter.v.m.car.ids");
    destination.searchParams.delete("page");
    return destination.pathname +
      (destination.searchParams.toString() ? `?${destination.searchParams.toString()}` : "");
  };

  const ensureClientVehicleChip = (actions, targetId) => {
    const selector =
      `[data-amp-remove-vehicle-filter][data-amp-vehicle-filter-id="${targetId}"]`;
    if (actions.querySelector(selector)) return;

    const remove = document.createElement("facet-remove");
    remove.dataset.ampClientVehicleChip = "true";
    const link = document.createElement("a");
    link.href = vehicleRemovalUrl();
    link.className = "collection-filter-button color-background-1";
    link.dataset.ampRemoveVehicleFilter = "true";
    link.dataset.ampVehicleFilterId = targetId;
    link.setAttribute("aria-label", `Remove vehicle filter ${targetId}`);
    const label = document.createElement("span");
    label.textContent = targetId;
    const dismiss = document.createElement("span");
    dismiss.className = "collection-filter-dismiss";
    dismiss.setAttribute("aria-hidden", "true");
    dismiss.textContent = "×";
    link.append(label, dismiss);
    remove.append(link);

    const firstDirectRemove = Array.from(actions.children).find(
      (child) => child.tagName?.toLowerCase() === "facet-remove",
    );
    actions.insertBefore(remove, firstDirectRemove || null);
  };

  const ensureClientClearFilters = (actions) => {
    let clear = actions.querySelector(".button-clear-collection-filters");
    if (!clear) {
      const remove = document.createElement("facet-remove");
      remove.dataset.ampClientClearFilters = "true";
      clear = document.createElement("a");
      clear.href = window.location.pathname;
      clear.className = "button-text-link button-clear-collection-filters";
      clear.textContent = "Clear filters";
      remove.append(clear);
      actions.append(remove);
    }
    clear.dataset.ampVehicleClearControl = "true";
    clear.dataset.ampClearAllFilters = "true";
    window.ampSyncPillAreaVehicleClearControls?.();
  };

  customElements.define(
    "vehicle-progressive-filters",
    class VehicleProgressiveFilters extends HTMLElement {
      connectedCallback() {
        if (this.hasAttribute("data-amp-client-vehicle-context")) {
          this.onClientVehicleContextSync = () => this.syncClientVehicleContext();
          window.addEventListener("pageshow", this.onClientVehicleContextSync);
          window.addEventListener("popstate", this.onClientVehicleContextSync);
          document.addEventListener(
            "collection:product-filters",
            this.onClientVehicleContextSync,
          );
          this.syncClientVehicleContext();
        }

        this.track = this.querySelector("[data-amp-guided-track]");
        this.previous = this.querySelector("[data-amp-guided-previous]");
        this.next = this.querySelector("[data-amp-guided-next]");
        this.position = this.querySelector("[data-amp-guided-position]");
        this.positionThumb = this.querySelector("[data-amp-guided-position-thumb]");
        if (!this.track || !this.previous || !this.next) return;

        this.onPrevious = () => this.scroll(-1);
        this.onNext = () => this.scroll(1);
        this.onTrackScroll = () => this.updateControls();
        this.onPointerDown = (event) => {
          if (event.pointerType === "touch" || event.button !== 0) return;
          this.dragPointerId = event.pointerId;
          this.dragPointerType = event.pointerType;
          this.dragStartX = event.clientX;
          this.dragStartY = event.clientY;
          this.dragStartScrollLeft = this.track.scrollLeft;
          this.hasDragged = false;
        };
        this.onPointerMove = (event) => {
          if (event.pointerId !== this.dragPointerId) return;
          const deltaX = event.clientX - this.dragStartX;
          const deltaY = event.clientY - this.dragStartY;
          if (!this.hasDragged && Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 5) return;
          if (
            !this.hasDragged &&
            this.dragPointerType === "touch" &&
            Math.abs(deltaY) > Math.abs(deltaX)
          ) {
            this.dragPointerId = null;
            return;
          }
          if (!this.hasDragged) {
            this.hasDragged = true;
            this.track.classList.add("is-dragging");
            this.track.setPointerCapture?.(event.pointerId);
          }
          this.track.scrollLeft = this.dragStartScrollLeft - deltaX;
          event.preventDefault();
        };
        this.onPointerEnd = (event) => {
          if (event.pointerId !== this.dragPointerId) return;
          if (this.hasDragged) {
            this.suppressNextClick = true;
            window.setTimeout(() => {
              this.suppressNextClick = false;
            }, 450);
          }
          this.track.classList.remove("is-dragging");
          if (this.track.hasPointerCapture?.(event.pointerId)) {
            this.track.releasePointerCapture(event.pointerId);
          }
          this.dragPointerId = null;
          this.dragPointerType = null;
        };
        this.onTrackClick = (event) => {
          if (!this.suppressNextClick) return;
          this.suppressNextClick = false;
          event.preventDefault();
          event.stopImmediatePropagation();
        };
        this.previous.addEventListener("click", this.onPrevious);
        this.next.addEventListener("click", this.onNext);
        this.track.addEventListener("scroll", this.onTrackScroll, { passive: true });
        this.track.addEventListener("click", this.onTrackClick, true);
        this.track.addEventListener("pointerdown", this.onPointerDown);
        this.track.addEventListener("pointermove", this.onPointerMove);
        this.track.addEventListener("pointerup", this.onPointerEnd);
        this.track.addEventListener("pointercancel", this.onPointerEnd);
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.track);
        requestAnimationFrame(() => this.updateControls());
      }

      disconnectedCallback() {
        window.removeEventListener("pageshow", this.onClientVehicleContextSync);
        window.removeEventListener("popstate", this.onClientVehicleContextSync);
        document.removeEventListener(
          "collection:product-filters",
          this.onClientVehicleContextSync,
        );
        this.previous?.removeEventListener("click", this.onPrevious);
        this.next?.removeEventListener("click", this.onNext);
        this.track?.removeEventListener("scroll", this.onTrackScroll);
        this.track?.removeEventListener("click", this.onTrackClick, true);
        this.track?.removeEventListener("pointerdown", this.onPointerDown);
        this.track?.removeEventListener("pointermove", this.onPointerMove);
        this.track?.removeEventListener("pointerup", this.onPointerEnd);
        this.track?.removeEventListener("pointercancel", this.onPointerEnd);
        this.resizeObserver?.disconnect();
      }

      syncClientVehicleContext() {
        const targetId = activeVehicleTargetId();
        const hasVehicleTarget = Boolean(targetId);
        const wrapper = this.closest(".collection-actions-wrapper");
        const actions = this.closest(".collection-actions-filters");

        this.hidden = !hasVehicleTarget;
        wrapper?.classList.toggle("has-vehicle-progressive-filters", hasVehicleTarget);
        if (hasVehicleTarget) wrapper?.classList.add("has-active-filters");

        document
          .querySelectorAll(
            '.amp-collection-filter-list > .js-filter[data-name="filter.p.m.custom.department"]',
          )
          .forEach((departmentFacet) => {
            if (hasVehicleTarget) {
              departmentFacet.style.setProperty("--amp-facet-order", "5");
            } else {
              departmentFacet.style.removeProperty("--amp-facet-order");
            }
          });
        actions
          ?.querySelectorAll("[data-amp-client-vehicle-selections]")
          .forEach((selection) => { selection.hidden = !hasVehicleTarget; });
        actions
          ?.querySelectorAll("[data-amp-progressive-filter-chip]")
          .forEach((chip) => { chip.hidden = hasVehicleTarget; });
        document
          .querySelectorAll("[data-amp-client-vehicle-sort-context]")
          .forEach((sortControl) => {
            sortControl.hidden =
              !hasVehicleTarget || Number(this.dataset.ampGuidedResultCount || 0) <= 1;
          });

        if (!hasVehicleTarget) {
          actions
            ?.querySelectorAll("[data-amp-client-vehicle-chip]")
            .forEach((chip) => chip.remove());
          return;
        }

        if (actions) {
          ensureClientVehicleChip(actions, targetId);
          ensureClientClearFilters(actions);
        }
        window.ampScheduleRegistrationVehicleChipDecoration?.();
      }

      scroll(direction) {
        const distance = Math.max(240, Math.round(this.track.clientWidth * 0.75));
        this.track.scrollBy({ left: distance * direction, behavior: "smooth" });
      }

      updateControls() {
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const scrollPosition = Math.min(maxScroll, Math.abs(this.track.scrollLeft));
        this.previous.disabled = scrollPosition <= 2;
        this.next.disabled = scrollPosition >= maxScroll - 2;
        this.querySelector(".amp-vehicle-progressive-filters__controls")?.toggleAttribute(
          "hidden",
          maxScroll <= 2,
        );

        if (this.position && this.positionThumb) {
          const viewportRatio = this.track.scrollWidth
            ? Math.min(1, this.track.clientWidth / this.track.scrollWidth)
            : 1;
          const thumbRatio = Math.max(0.12, viewportRatio);
          const progress = maxScroll > 0 ? scrollPosition / maxScroll : 0;
          this.positionThumb.style.inlineSize = `${thumbRatio * 100}%`;
          this.positionThumb.style.insetInlineStart = `${progress * (1 - thumbRatio) * 100}%`;
          this.position.toggleAttribute("hidden", maxScroll <= 2);
        }
      }
    },
  );
}

if (!customElements.get("vehicle-progressive-filter-selections")) {
  customElements.define(
    "vehicle-progressive-filter-selections",
    class VehicleProgressiveFilterSelections extends HTMLElement {
      connectedCallback() {
        this.onSelectionClick = (event) => {
          const departmentLink = event.target.closest("[data-amp-guided-clear-department]");
          const productTypeLink = event.target.closest("[data-amp-guided-clear-product-type]");
          const link = departmentLink || productTypeLink;
          if (!link || !this.contains(link)) return;

          const destination = new URL(link.href, window.location.href);
          Array.from(destination.searchParams.keys()).forEach((key) => {
            const isRefinement = /^filter\.p\.(?:m|metafield)\.custom\.refinement_\d{2}$/.test(
              key,
            );
            const isProductType =
              /^filter\.p\.(?:m|metafield)\.custom\.product_type$/.test(key);
            if (isRefinement || (departmentLink && isProductType)) {
              destination.searchParams.delete(key);
            }
          });
          destination.searchParams.delete("page");

          event.preventDefault();
          event.stopImmediatePropagation();
          const searchParams = destination.searchParams.toString();
          if (typeof FacetFiltersForm !== "undefined") {
            FacetFiltersForm.renderPage(searchParams, event);
          } else {
            window.location.assign(destination.pathname + (searchParams ? `?${searchParams}` : ""));
          }
        };

        this.addEventListener("click", this.onSelectionClick, true);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.onSelectionClick, true);
      }
    },
  );
}
